import { useCallback, useEffect, useState } from 'react';
import toolkitService from '../../services/ToolkitService';
import useToolkitBatch, { sourceRow } from './useToolkitBatch';

// A batch whose rows are KEPT in toolkit storage under the project's property (toolkit storage rows-*): loaded when
// the screen opens, replaced when a new file is loaded, and saved row by row as each is processed — so a run stopped
// half way, or picked up by a colleague, carries on where it was. `tool` names the list ('block-grid', 'event-mgmt').
//
// Rows need a stable `rowId`. Returns the batch plus `replace(rows)`, `saveRow(row)` and a `message`.
//
// `run` is the screen's processRow — or { request, interpret?, server } so its rows can also Run on server
// (useToolkitBatch); the job then keeps the stored rows current itself (afterItem rows-save).

const storedRow = (row) => sourceRow(row, ['steps']);

const useStoredRows = (ctx, ready, tool, run) => {
    const [message, setMessage] = useState('');
    const saveRow = useCallback((row) =>
        toolkitService.storage(ctx, 'rows-save', { tool, row: { ...storedRow(row), status: row.status, errorMessage: row.errorMessage } }),
    [ctx, tool]);
    const batch = useToolkitBatch(typeof run === 'function'
        ? { processRow: run, onRowDone: saveRow }
        : { ...run, ctx, onRowDone: saveRow, server: run.server && { ...run.server, save: storedRow, afterItem: 'rows-save', tool } });
    const { load } = batch;

    useEffect(() => {
        if (!ready) return;
        toolkitService.storage(ctx, 'rows-get-all', { tool })
            .then(({ data }) => {
                const rows = data.result || [];
                load(rows);
                setMessage(rows.length ? `${rows.length} row(s) restored from the last run at this property.` : '');
            })
            .catch((err) => setMessage(err?.response?.data?.message || 'Could not load the saved rows for this property.'));
    }, [ctx, ready, tool, load]);

    const replace = useCallback(async (rows) => {
        load(rows);
        try {
            await toolkitService.storage(ctx, 'rows-replace', { tool, rows: rows.map((r) => sourceRow(r)) });
            setMessage('');
        } catch (err) {
            setMessage(`Loaded, but not saved for resuming: ${err?.response?.data?.message || err.message}`);
        }
    }, [ctx, tool, load]);

    return { batch, replace, saveRow, message };
};

export default useStoredRows;
